import { z } from "zod";
import { DrawerHand } from "./photoAssign.js";
import { ColorMode, ThemeColor } from "./theme.js";

// ---------------------------------------------------------------------------
// 人（accounts）ごとの画面の好み（#115、docs/44 §2・§8、DB 20261003_1000）。
//   文字の大きさ（大・中・小）。決まる順は 人の設定 → 端末の Cookie gb_text → 既定（PC版＝大、スマホ版＝中）。
//   大＝今の PC の大きさ。変わるのは文字だけで、高さ・余白・押すところは変えない（styles.css の --fs-N）。
//   版（スマホ版／PC版）は端末ごと（Cookie gb_view だけ。人には持たない）。
//   docs/44 §5 の「引き出しの左右」もここ（preferences）に足す
// ---------------------------------------------------------------------------

/** l＝大（今の PC）／m＝中（スマホの既定）／s＝小 */
export const TextSize = z.enum(["l", "m", "s"]);
export type TextSize = z.infer<typeof TextSize>;

/** 画面に並べる順（小・中・大）と呼び名 */
export const TEXT_SIZES: readonly TextSize[] = ["s", "m", "l"];
export const TEXT_SIZE_LABEL: Record<TextSize, string> = { s: "小", m: "中", l: "大" };

/**
 * 見積書（PC）の表の列の幅（#140、docs/47 §15）。人ごとに変えられるのは 規格・仕様 と 備考 だけ（名称が残りを吸収する。社内用の列は変えない）。
 *   見出しの右端の境目をドラッグ／見出しの「…」の 狭い・標準・広い・既定に戻す。新しい見積・別の PC でも同じ幅（人＝accounts に保存）。
 *   上限は「1366px の画面（メニュー 56px・Windows の縦のスクロールバー 17px）で社内用の列を出しても表が横に流れない」幅＝規格＋備考で sum まで
 */
export const ESTIMATE_COLUMN_LIMITS = {
  spec: { min: 120, def: 200, wide: 300 },
  note: { min: 80, def: 100, wide: 200 },
  sum: 440,
} as const;
const L_ = ESTIMATE_COLUMN_LIMITS;
export const EstimateColumns = z
  .object({
    spec: z.number().int().min(L_.spec.min).max(L_.sum - L_.note.min),
    note: z.number().int().min(L_.note.min).max(L_.sum - L_.spec.min),
  })
  .refine((x) => x.spec + x.note <= L_.sum, { message: `{lineSpec}と{lineNote}の幅は合わせて ${L_.sum}px までです` });
export type EstimateColumns = z.infer<typeof EstimateColumns>;
/** 規格・仕様と備考の幅を上限の中に収める。動かした側（moved）を、もう片方の分を差し引いた幅までにする（もう片方は変えない） */
export function clampEstimateColumns(x: { spec: number; note: number }, moved: "spec" | "note" = "spec"): EstimateColumns {
  const r = (v: number) => Math.round(v);
  let spec = Math.min(Math.max(r(x.spec), L_.spec.min), L_.sum - L_.note.min);
  let note = Math.min(Math.max(r(x.note), L_.note.min), L_.sum - L_.spec.min);
  if (spec + note > L_.sum) {
    if (moved === "spec") spec = L_.sum - note;
    else note = L_.sum - spec;
  }
  return { spec, note };
}

/** Me.preferences（null＝人の設定なし＝端末の Cookie か既定） */
export const AccountPreferences = z.object({
  textSize: TextSize.nullable(),
  /** 写真の引き出しの向き（#118）。null＝人の設定なし（右手） */
  drawerHand: DrawerHand.nullable().default(null),
  /** 見積書（PC）の表の 規格・仕様／備考 の幅（#140）。null＝人の設定なし（既定の 200／100） */
  estimateColumns: EstimateColumns.nullable().default(null),
  /** 見積依頼書の表の 規格／備考 の幅（#220。10/20 に見積書と分けた）。null＝人の設定なし（見積書の幅＝estimateColumns に従う） */
  rfqColumns: EstimateColumns.nullable().default(null),
  /** テーマ色（#191、docs/77）。null＝人の設定なし（製品の既定＝PRODUCTS[*].brand.theme） */
  themeColor: ThemeColor.nullable().default(null),
  /** 明るさ（#191 の2回目、docs/77 §8）。null＝人の設定なし（ライト） */
  colorMode: ColorMode.nullable().default(null),
});
export type AccountPreferences = z.infer<typeof AccountPreferences>;

/** PUT /account/preferences。省いた項目は変えない。null で人の設定を消す（端末の Cookie か既定に戻る） */
export const AccountPreferencesUpdate = z.object({
  textSize: TextSize.nullable().optional(),
  drawerHand: DrawerHand.nullable().optional(),
  estimateColumns: EstimateColumns.nullable().optional(),
  rfqColumns: EstimateColumns.nullable().optional(),
  themeColor: ThemeColor.nullable().optional(),
  colorMode: ColorMode.nullable().optional(),
});
export type AccountPreferencesUpdate = z.infer<typeof AccountPreferencesUpdate>;

/** 版の切替と文字サイズの文（docs/10 §5.1.2。PC版・スマホ版で同じ） */
export const VIEW_MSG = {
  textSize: "文字サイズ",
  toPc: "PC版で表示",
  toPhone: "スマホ版で表示",
  /** スマホ版のログイン前の下の帯（上の帯の「トップページへ」の代わり） */
  topPage: "トップページ",
} as const;
